<template>
  <v-select
    v-model="selectedGPU"
    label="GPU"
    :items="GPUS"
    item-title="name"
    clearable
    @click:clear="
      selectedGPU = null;
      selectedOutput = null;
    "
    item-value="template.index"></v-select>
  <v-select
    :disabled="selectedGPU === null"
    v-model="selectedOutput"
    label="Output"
    clearable
    @click:clear="selectedOutput = null"
    :items="GPUS[selectedGPU]?.template.streams"
    item-title="name"
    item-value="template.jacks"></v-select>

  <v-btn
    variant="flat"
    color="primary"
    :loading="setBtnLoading"
    :disabled="selectedGPU === null || selectedOutput === null"
    @click="() => setGPUAndOutput(currentConfigName)">
    Save Changes
  </v-btn>
</template>
<script setup>
import {
  updateConfig,
  getConfig,
  getGPUInfo,
  getCurrentConfigName,
} from '../../utils/ventuzConfig';
import { ref, watch, onMounted } from 'vue';

/**
 * @typedef { import("vue").Ref } Ref
 */

const setBtnLoading = ref(false);

const selectedGPU = ref(null);
const selectedOutput = ref(null);
const currentConfigName = ref(null);
const currentConfig = ref({});
onMounted(async () => {
  try {
    currentConfigName.value = await getCurrentConfigName();
  } catch (e) {
    console.error('Failed to get current config name', e);
    currentConfigName.value = null;
  }

  try {
    currentConfig.value = currentConfigName.value
      ? await getConfig(currentConfigName.value)
      : {};
  } catch (e) {
    console.error('Failed to get current config', e);
    currentConfig.value = {};
  }

  try {
    // ensure GPUS prop is available before calling getGpu
    getGpu();
    setOutput();
  } catch (e) {
    console.warn('getGpu or setOutput failed', e);
  }
});

function setOutput() {
  selectedOutput.value = currentConfig?.value?.video?.[2].streams?.[0] || null;
}
const props = defineProps(['GPUS', 'currentConfigName']);

const emit = defineEmits(['updateConfig', 'updateGPUS']);

function getGpu() {
  for (const gpu of props.GPUS) {
    if (gpu.name === currentConfig.value.video[2].name) {
      selectedGPU.value = Object.keys(props.GPUS).findIndex(
        (key) => props.GPUS[key].name === gpu.name
      );
      break;
    }
  }
}

watch(props.GPUS, () => {
  console.log('GPUS changes', props.GPUS);
});

/**
 * @param {string} configName - Name of the current config.
 * @param {string} currentConfigName - Ref to update.
 */
async function setGPUAndOutput(configName) {
  setBtnLoading.value = true;
  const data = buildGPUAndOutputFromSelection();
  const response = await updateConfig(configName, data);
  emit('updateConfig', await getConfig(configName));
  emit('updateGPUS', await getGPUInfo());
  selectedGPU.value = null;
  selectedOutput.value = null;

  setBtnLoading.value = false;
  getGpu();
  return response;
}
function buildGPUAndOutputFromSelection() {
  if (selectedGPU.value === null || selectedOutput.value === null) {
    return;
  }

  const gpu = props.GPUS.filter(
    (gpu) => gpu.template.index === selectedGPU.value
  )[0];

  const output = gpu.template.streams.filter(
    (stream) => stream.template.jacks === selectedOutput.value
  )[0];

  const data = {
    video: [
      {
        class: gpu.template.class,
        name: gpu.template.name,
        index: gpu.template.index,
        pcVideoNorms: gpu.template.pcVideoNorms,
        streams: [
          {
            name: output.template.name,
            mode: output.template.mode,
            jacks: output.template.jacks,
            videoRaster: output.template.videoRaster,
            videoSizeX: output.template.videoSizeX,
            videoSizeY: output.template.videoSizeY,
            options: output.template.options,
            synchronized: output.template.synchronized,
          },
        ],
      },
    ],
  };

  return data;
}
</script>
<style></style>
